import { useEffect } from "react";
import { create } from "zustand";
import { USER_TYPES } from "@/shared/constants";
import { useAuth } from "@/shared/stores/useAuth";
import {
  isOrganizationWorkspaceActive,
  readOrganizationSession,
  setOrganizationWorkspaceActive,
} from "./organizationSession";
import type { OrganizationRecord, OrganizationSession } from "./types";

interface OrganizationWorkspaceState {
  session: OrganizationSession | null;
  setSession: (session: OrganizationSession | null) => void;
  hydrated: boolean;
  setHydrated: (value: boolean) => void;
}

const useOrganizationWorkspaceStore = create<OrganizationWorkspaceState>()((set) => ({
  session: null,
  setSession: (session) => set(() => ({ session })),
  hydrated: false,
  setHydrated: (value) => set(() => ({ hydrated: value })),
}));

export function useCurrentOrganization() {
  const { userInfo, isOrganizationLogin } = useAuth();
  const { session, setSession, hydrated, setHydrated } = useOrganizationWorkspaceStore();

  useEffect(() => {
    if (hydrated) return;
    if (isOrganizationLogin || isOrganizationWorkspaceActive()) {
      setSession(readOrganizationSession());
    }
    setHydrated(true);
  }, [hydrated, isOrganizationLogin, setHydrated, setSession]);

  const enterOrganization = (next: OrganizationSession) => {
    setOrganizationWorkspaceActive(true);
    setSession(next);
  };

  const leaveOrganization = () => {
    if (userInfo?.account_type === USER_TYPES.ORGANIZATION) return;
    setOrganizationWorkspaceActive(false);
    setSession(null);
  };

  const isOrganizationWorkspace = isOrganizationLogin || Boolean(session?.organization?.id);

  return {
    organization: (session?.organization ?? null) as OrganizationRecord | null,
    session,
    isOrganizationWorkspace,
    enterOrganization,
    leaveOrganization,
  };
}
